import { useMemo } from "react";
import { CpuChipIcon, WrenchIcon, CheckCircleIcon, XCircleIcon } from '../../../../automationStudioIcons';
import { LABELS } from '../../../../content';
import DynamicIntentDisplay from '../../../shared/DynamicIntentDisplay';
import { parseDynamicIntentLabel } from '../../../../../AutomationStudio/utils/stepMetaInfo';
import styles from './StepAgentResultTrace.module.scss';

function isResponseStatusError(turn) {
    if (!turn || typeof turn !== "object") return false;
    const s = String(turn.responseStatus ?? "ok").toLowerCase();
    return ["error", "failed", "fail", "failure"].includes(s);
}

function hasThinkingContent(turn) {
    const t = turn?.thinking;
    if (t == null) return false;
    return String(t).trim().length > 0;
}

function buildTimelineRows(turns) {
    const rows = [];
    turns.forEach((turn) => {
        if (hasThinkingContent(turn)) {
            rows.push({ key: `${turn.id}-thinking`, phase: "thinking", turn });
        }
        if (turn.finalizeSummaryOnly) {
            rows.push({ key: `${turn.id}-summary`, phase: "finalizeSummary", turn });
            return;
        }
        rows.push({ key: `${turn.id}-tool`, phase: "tool", turn });
        if (!turn.hideResponseRow) {
            rows.push({ key: `${turn.id}-response`, phase: "response", turn });
        }
    });
    return rows;
}

function PhaseNode({ phase, responseError }) {
    if (phase === "thinking") {
        const inner = `${styles.nodeInner} ${styles[`nodePhase${phase}`]}`;
        return (
            <span className={inner}>
                <CpuChipIcon className={styles.nodeGlyph} aria-hidden />
            </span>
        );
    }
    if (phase === "tool") {
        const inner = `${styles.nodeInner} ${styles[`nodePhase${phase}`]}`;
        return (
            <span className={inner}>
                <WrenchIcon className={styles.nodeGlyph} aria-hidden />
            </span>
        );
    }
    if (phase === "finalizeSummary") {
        const inner = responseError
            ? `${styles.nodeInner} ${styles.nodePhaseresponseError}`
            : `${styles.nodeInner} ${styles.nodePhaseresponse}`;
        return (
            <span className={inner}>
                {responseError ? (
                    <XCircleIcon className={styles.nodeGlyph} aria-hidden />
                ) : (
                    <CheckCircleIcon className={styles.nodeGlyph} aria-hidden />
                )}
            </span>
        );
    }
    const inner = responseError
        ? `${styles.nodeInner} ${styles.nodePhaseresponseError}`
        : `${styles.nodeInner} ${styles.nodePhaseresponse}`;
    return (
        <span className={inner}>
            {responseError ? (
                <XCircleIcon className={styles.nodeGlyph} aria-hidden />
            ) : (
                <CheckCircleIcon className={styles.nodeGlyph} aria-hidden />
            )}
        </span>
    );
}

function TraceCard({ title, children, bodyClassName = "", phase, responseError = false }) {
    let phaseStyle = styles[`traceCard${phase}`];
    if (phase === "response") {
        phaseStyle = responseError ? styles.traceCardresponseError : styles.traceCardresponse;
    } else if (phase === "finalizeSummary") {
        phaseStyle = responseError ? styles.traceCardresponseError : styles.traceCardresponse;
    }
    const cardClass = `${styles.traceCard} ${phaseStyle}`.trim();
    return (
        <div className={cardClass}>
            <h3 className={styles.traceCardHeading}>{title}</h3>
            <div className={`${styles.traceCardBody} ${bodyClassName}`.trim()}>{children}</div>
        </div>
    );
}

export default function StepAgentResultTrace({ agentTraceTurns, isFromLambda = false, intent = "" }) {
    const turnsForTimeline = useMemo(() => {
        if (Array.isArray(agentTraceTurns) && agentTraceTurns.length > 0) return agentTraceTurns;
        return [];
    }, [agentTraceTurns]);

    const rows = buildTimelineRows(turnsForTimeline);

    const { type: intentType, intent: intentText } = useMemo(
        () => parseDynamicIntentLabel(intent),
        [intent]
    );

    const hasRows = rows.length > 0;

    if (!isFromLambda && !hasRows) {
        return null;
    }

    const timelineSectionClass = isFromLambda && hasRows
        ? `${styles.traceTimelineSection} ${styles.traceTimelineSectionWithIntent}`
        : styles.traceTimelineSection;

    return (
        <div className={styles.root}>
            {isFromLambda ? (
                <DynamicIntentDisplay
                    intentType={intentType}
                    intentText={intentText}
                    headingId="agent-trace-dynamic-intent"
                    className={styles.intentBlock}
                />
            ) : null}
            {hasRows ? (
                <div className={timelineSectionClass}>
                {rows.map((row, index) => {
                    const { turn, phase } = row;
                    const isFirst = index === 0;
                    const isLast = index === rows.length - 1;
                    const responseError = (phase === "response" || phase === "finalizeSummary") && isResponseStatusError(turn);
                    let title;
                    let body;
                    let bodyClass = "";
                    if (phase === "thinking") {
                        title = LABELS.AGENT_TRACE_THINKING;
                        body = turn.thinking || "";
                        bodyClass = styles.thinkingBody;
                    } else if (phase === "finalizeSummary") {
                        title = LABELS.AGENT_TRACE_SUMMARY;
                        body = turn.finalizeSummaryBody || "";
                        bodyClass = responseError ? styles.summaryBodyError : styles.summaryBody;
                    } else if (phase === "tool") {
                        title = turn.toolName || "—";
                        body = turn.toolCallBody || "";
                        bodyClass = styles.toolTraceBody;
                    } else {
                        title = LABELS.AGENT_TRACE_RESPONSE;
                        body = turn.responseBody || "";
                        bodyClass = styles.toolTraceBody;
                    }

                    return (
                        <div key={row.key} className={styles.eventRow}>
                            <div className={styles.nodeColumn}>
                                {!isFirst ? <div className={styles.railIncoming} aria-hidden /> : null}
                                <div className={!isFirst ? styles.phaseAnchorOverlap : styles.phaseAnchor}>
                                    <PhaseNode phase={phase} responseError={responseError} />
                                </div>
                                {!isLast ? <div className={styles.railFill} aria-hidden /> : null}
                            </div>
                            <div className={styles.cardColumn}>
                                <TraceCard
                                    title={title}
                                    bodyClassName={bodyClass}
                                    phase={phase}
                                    responseError={responseError}
                                >
                                    {phase === "tool" && Array.isArray(turn.toolCallBodyLines) && turn.toolCallBodyLines.length > 0 ? (
                                        <div className={styles.toolTraceLabeledLines}>
                                            {turn.toolCallBodyLines.map((ln, i) => (
                                                <span key={i} className={styles.toolTraceLine}>
                                                    {ln.label ? <span className={styles.toolTraceKey}>{ln.label}</span> : null}
                                                    {ln.value}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        body
                                    )}
                                </TraceCard>
                            </div>
                        </div>
                    );
                })}
                </div>
            ) : null}
        </div>
    );
}
